import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type RefObject } from 'react';

/**
 * The one floating primitive for Zagru One. Every overlay (Select, Combobox, MultiSelect, Menu,
 * Tooltip, Popover, DatePicker, TimePicker, column menus…) positions through this hook — there is
 * deliberately no second implementation and no third-party positioning dependency (ADR-0011).
 *
 * Features: placement with flip and shift, arrow placement, size matching / max-height clamping,
 * scroll + resize tracking, and the enter offset the panel animates out of (`--zo-enter-x/y`).
 */

export type Side = 'top' | 'bottom' | 'left' | 'right';
export type Align = 'start' | 'center' | 'end';

export type Placement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

export interface UseFloatingOptions {
  /** Preferred placement; flipped to the opposite side when it does not fit. */
  placement?: Placement;
  /** Gap between anchor and floating element, in px. */
  offset?: number;
  /** Minimum distance kept from the viewport edge. */
  padding?: number;
  /** Flip to the opposite side when the preferred one does not fit (default true). */
  flip?: boolean;
  /** Slide along the cross axis to stay in the viewport (default true). */
  shift?: boolean;
  /** Set `min-width` to the anchor's width (listbox panels). */
  matchWidth?: boolean;
  /** Set `width` to the anchor's width exactly. */
  matchWidthExact?: boolean;
  /** Clamp `max-height` to the space available on the resolved side. */
  size?: boolean;
  /** Arrow element to position against the anchor's centre. */
  arrowRef?: RefObject<HTMLElement | null>;
  /** Distance the arrow keeps from the panel's corners. */
  arrowPadding?: number;
  /** Stacking order applied to the panel. */
  zIndex?: number | string;
}

export interface FloatingState {
  /** Fixed-position style for the floating element, including the enter-offset custom properties. */
  style: CSSProperties;
  /** Absolute style for the arrow element (empty when no `arrowRef` was given). */
  arrowStyle: CSSProperties;
  /** Placement actually used after flipping. */
  placement: Placement;
  side: Side;
  align: Align;
  /** True once a real measurement has happened. */
  ready: boolean;
  /** Force a reposition (after content changes size, for example). */
  update: () => void;
}

function splitPlacement(placement: Placement): { side: Side; align: Align } {
  const [side, align] = placement.split('-') as [Side, Align | undefined];
  return { side, align: align ?? 'center' };
}

function joinPlacement(side: Side, align: Align): Placement {
  return (align === 'center' ? side : `${side}-${align}`) as Placement;
}

const OPPOSITE: Record<Side, Side> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

/** Layout size, unaffected by the enter animation's transform (unlike `getBoundingClientRect`). */
function layoutSize(el: HTMLElement): { width: number; height: number } {
  const w = el.offsetWidth || el.getBoundingClientRect().width;
  const h = el.offsetHeight || el.getBoundingClientRect().height;
  return { width: w, height: h };
}

export function useFloating(
  anchorRef: RefObject<HTMLElement | null>,
  floatingRef: RefObject<HTMLElement | null>,
  open: boolean,
  options: UseFloatingOptions = {},
): FloatingState {
  const {
    placement: preferred = 'bottom-start',
    offset = 6,
    padding = 8,
    flip = true,
    shift = true,
    matchWidth = false,
    matchWidthExact = false,
    size = false,
    arrowRef,
    arrowPadding = 12,
    zIndex,
  } = options;

  const [state, setState] = useState<{
    top: number;
    left: number;
    placement: Placement;
    width?: number;
    maxHeight?: number;
    arrow: CSSProperties;
    ready: boolean;
  }>({ top: 0, left: 0, placement: preferred, arrow: {}, ready: false });

  // Options land in a ref so callers may pass inline objects without re-running the effect.
  const optsRef = useRef({ preferred, offset, padding, flip, shift, matchWidth, matchWidthExact, size, arrowPadding });
  optsRef.current = { preferred, offset, padding, flip, shift, matchWidth, matchWidthExact, size, arrowPadding };

  const frame = useRef<number | null>(null);

  const compute = useCallback(() => {
    const anchor = anchorRef.current;
    const floating = floatingRef.current;
    if (!anchor || !floating) return;
    const o = optsRef.current;
    const a = anchor.getBoundingClientRect();
    const f = layoutSize(floating);
    const vw = typeof window === 'undefined' ? 1024 : window.innerWidth || 1024;
    const vh = typeof window === 'undefined' ? 768 : window.innerHeight || 768;

    const { align } = splitPlacement(o.preferred);
    let { side } = splitPlacement(o.preferred);

    const room: Record<Side, number> = {
      top: a.top,
      bottom: vh - a.bottom,
      left: a.left,
      right: vw - a.right,
    };

    // ---- flip ----
    if (o.flip) {
      const needed = (side === 'top' || side === 'bottom' ? f.height : f.width) + o.offset + o.padding;
      if (room[side] < needed && room[OPPOSITE[side]] > room[side]) side = OPPOSITE[side];
    }

    // ---- main axis ----
    let top: number;
    let left: number;
    if (side === 'bottom') top = a.bottom + o.offset;
    else if (side === 'top') top = a.top - f.height - o.offset;
    else top = a.top + a.height / 2 - f.height / 2;

    if (side === 'right') left = a.right + o.offset;
    else if (side === 'left') left = a.left - f.width - o.offset;
    else left = a.left + a.width / 2 - f.width / 2;

    // ---- cross axis alignment ----
    if (side === 'top' || side === 'bottom') {
      if (align === 'start') left = a.left;
      else if (align === 'end') left = a.right - f.width;
    } else {
      if (align === 'start') top = a.top;
      else if (align === 'end') top = a.bottom - f.height;
    }

    // ---- shift into the viewport ----
    if (o.shift) {
      if (side === 'top' || side === 'bottom') {
        left = Math.min(Math.max(left, o.padding), Math.max(o.padding, vw - f.width - o.padding));
      } else {
        top = Math.min(Math.max(top, o.padding), Math.max(o.padding, vh - f.height - o.padding));
      }
    }

    // ---- size ----
    let maxHeight: number | undefined;
    if (o.size) {
      const available = side === 'top' ? a.top - o.offset - o.padding : side === 'bottom' ? vh - a.bottom - o.offset - o.padding : vh - 2 * o.padding;
      maxHeight = Math.max(120, Math.round(available));
    }

    // ---- arrow ----
    let arrow: CSSProperties = {};
    if (arrowRef?.current) {
      const arrowEl = arrowRef.current;
      const as = layoutSize(arrowEl);
      const half = (side === 'top' || side === 'bottom' ? as.width : as.height) / 2;
      if (side === 'top' || side === 'bottom') {
        const centre = a.left + a.width / 2 - left;
        const x = Math.min(Math.max(centre, o.arrowPadding + half), Math.max(o.arrowPadding + half, f.width - o.arrowPadding - half));
        arrow = { left: Math.round(x - half), [side === 'bottom' ? 'top' : 'bottom']: -half };
      } else {
        const centre = a.top + a.height / 2 - top;
        const y = Math.min(Math.max(centre, o.arrowPadding + half), Math.max(o.arrowPadding + half, f.height - o.arrowPadding - half));
        arrow = { top: Math.round(y - half), [side === 'right' ? 'left' : 'right']: -half };
      }
    }

    const next = {
      top: Math.round(top),
      left: Math.round(left),
      placement: joinPlacement(side, align),
      width: o.matchWidthExact || o.matchWidth ? Math.round(a.width) : undefined,
      maxHeight,
      arrow,
      ready: true,
    };
    setState((prev) =>
      prev.ready === next.ready &&
      prev.top === next.top &&
      prev.left === next.left &&
      prev.placement === next.placement &&
      prev.width === next.width &&
      prev.maxHeight === next.maxHeight &&
      JSON.stringify(prev.arrow) === JSON.stringify(next.arrow)
        ? prev
        : next,
    );
  }, [anchorRef, floatingRef, arrowRef]);

  const schedule = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      compute();
      return;
    }
    if (frame.current != null) return;
    frame.current = window.requestAnimationFrame(() => {
      frame.current = null;
      compute();
    });
  }, [compute]);

  useLayoutEffect(() => {
    if (!open) {
      setState((prev) => (prev.ready ? { ...prev, ready: false } : prev));
      return;
    }
    compute();
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(schedule);
      if (floatingRef.current) ro.observe(floatingRef.current);
      if (anchorRef.current) ro.observe(anchorRef.current);
    }
    return () => {
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      ro?.disconnect();
      if (frame.current != null) {
        window.cancelAnimationFrame(frame.current);
        frame.current = null;
      }
    };
  }, [open, compute, schedule, anchorRef, floatingRef]);

  const { side, align } = splitPlacement(state.placement);

  const style = useMemo<CSSProperties>(() => {
    const enterX = side === 'left' ? offset : side === 'right' ? -offset : 0;
    const enterY = side === 'top' ? offset : side === 'bottom' ? -offset : 0;
    return {
      position: 'fixed',
      top: state.top,
      left: state.left,
      ...(matchWidthExact ? { width: state.width } : matchWidth ? { minWidth: state.width } : {}),
      ...(state.maxHeight != null ? { maxHeight: state.maxHeight } : {}),
      ...(zIndex != null ? { zIndex } : {}),
      // Consumed by the `.zo-floating` enter transition: the panel grows out of its trigger.
      ['--zo-enter-x' as string]: `${enterX}px`,
      ['--zo-enter-y' as string]: `${enterY}px`,
    };
  }, [side, offset, state.top, state.left, state.width, state.maxHeight, matchWidth, matchWidthExact, zIndex]);

  return { style, arrowStyle: state.arrow, placement: state.placement, side, align, ready: state.ready, update: schedule };
}

/** True when the user asked for reduced motion. Re-evaluates when the preference changes. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => prefersReducedMotion());
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Resolved duration of a motion token in ms. Returns 0 when the stylesheet is absent (tests, SSR)
 * or the user prefers reduced motion, which makes overlay exits synchronous.
 */
export function motionDuration(token = '--zo-motion-overlay'): number {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  if (prefersReducedMotion()) return 0;
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
    if (!raw) return 0;
    const n = raw.endsWith('ms') ? Number.parseFloat(raw) : raw.endsWith('s') ? Number.parseFloat(raw) * 1000 : Number.NaN;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export type PresenceState = 'open' | 'closed';

export interface Presence {
  /** Whether the overlay should be in the DOM (stays true through the exit animation). */
  mounted: boolean;
  /** Drives `data-state` on the panel. */
  state: PresenceState;
}

/**
 * Keeps an overlay mounted for the length of its exit animation. When motion is disabled
 * (reduced motion, or no stylesheet as in tests) the unmount is synchronous.
 */
export function usePresence(open: boolean, token = '--zo-motion-overlay'): Presence {
  const [mounted, setMounted] = useState(open);
  const [state, setState] = useState<PresenceState>(open ? 'open' : 'closed');

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    setState('closed');
    const duration = motionDuration(token);
    if (duration <= 0) {
      setMounted(false);
      return;
    }
    const timer = setTimeout(() => setMounted(false), duration);
    return () => clearTimeout(timer);
  }, [open, token]);

  useEffect(() => {
    if (!open || !mounted) return;
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      setState('open');
      return;
    }
    const raf = window.requestAnimationFrame(() => setState('open'));
    return () => window.cancelAnimationFrame(raf);
  }, [open, mounted]);

  return { mounted, state: open ? state : 'closed' };
}
